import {
  ajax,
  getLanguageISO
} from 'application/src/js/functions.js';

import {
  getPriceDevelopmentIndicator
} from './functions.js';

import AdditionalChart from './additional-chart.js';

/**
 * @class
 * @param {number} args.rawmaterialid
 * @param {object} args.colors
 * @param {function} args.drawLimitLines
 * @param {function} args.showTooltip
 * @param {object} args.translations
 * @param {Mediator} args.mediator
 */
export default class Charts {
  constructor(args) {
    this.rawmaterialid = args.rawmaterialid;
    this.colors = args.colors;
    this.drawLimitLines = args.drawLimitLines;
    this.showTooltip = args.showTooltip;
    this.translations = args.translations;
    this.mediator = args.mediator;

    this.container = document.querySelector('.lp-content .charts');
    this.chartWrap = this.container.querySelector('.chart-wrap');
    this.additionalChartsContainer = this.container.querySelector('.additional');
    this.additionalChartsList = this.container.querySelector('.additional .list');
    this.chartHead = this.container.querySelector('.chart-head');
    this.chartCanvas = this.container.querySelector('.chart');
    this.verticalLineCanvas = this.container.querySelector('.vertical-line');

    this.requestUrl = '/app/ajax/ajaxLoadRelatedCharts';
    this.debugMode = true;
    this.hideClass = 'hide';
    this.chart = null;
    this.minimizedChart = window.innerWidth < 800;
    this.currency = 'EUR';
    this.showTooltipTimeout = null;

    let startDate = new Date();
    startDate.setHours(0, 0, 0, 0);
    startDate.setFullYear(startDate.getFullYear() - 1);

    let endDate = new Date();
    endDate.setHours(23, 59, 59, 999);

    this.load(startDate, endDate)
      .then(response => {
        this.user = response.user;
        this.env = response.env || {
          locale: languageIso
        };
        this.primaryChartData = response.data.stocksdata;
        this.relatedStocksData = response.data.relatedStocks;
        this.render();
        this.initChart();
        this.addEvents();
        if (this.primaryChartData.chartdata.length) this.container.classList.remove(this.hideClass);
        this.mediator.publish('finishedLoading');
      })
      .catch(e => {
        if (this.debugMode && e) console.log(e);
        this.mediator.publish('finishedLoading');
      });
  }

  load(startDate, endDate) {
    this.mediator.publish('startedLoading');

    return ajax(this.requestUrl, {
      rawmaterialid: this.rawmaterialid,
      startdate: moment(startDate).format('YYYY-MM-DD'),
      endDate: moment(endDate).format('YYYY-MM-DD'),
      currency: this.currency,
      num_related: 4
    });
  }

  addEvents() {
    this.chartCanvas.addEventListener('mousemove', e => this.drawVerticalLine(e));

    window.addEventListener('resize', e => {
      if (window.innerWidth < 800 && !this.minimizedChart) {
        this.minimizedChart = true;
        this.shrinkChart();
      }

      if (window.innerWidth > 800 && this.minimizedChart) {
        this.minimizedChart = false;
        this.enlargeChart();
      }
    });
  }

  render() {
    // render the chart head
    this.chartHead.innerHTML = this.chartHeadTemplate;

    // display only even amount of charts
    if (this.relatedStocksData.length % 2 > 0) this.relatedStocksData.pop();

    // show container if at least 1 chart
    this.additionalChartsContainer.classList[this.relatedStocksData.length ? 'remove' : 'add'](this.hideClass);
    this.additionalChartsList.innerHTML = '';

    this.relatedStocksData.map(stockData => {
      const additionalChartElement = document.createElement('div');
      this.additionalChartsList.insertAdjacentElement('beforeend', additionalChartElement);

      new AdditionalChart({
        parent: this,
        element: additionalChartElement,
        data: stockData,
        measurement: this.measurement,
        user: this.user,
        env: this.env,
        translations: this.translations,
        getPriceDevelopmentIndicator: getPriceDevelopmentIndicator
      });
    });
  }

  get chartHeadTemplate() {
    return `
      <div class="item-inner">
        <div class="item-image">
          <img alt="" src="${this.primaryChartData.image_path}" alt="${this.primaryChartData.fullname}">

          ${this.primaryChartData.is_bio ? `
            <div class="bio-icon inline">
              <i class="fas fa-leaf"></i>
              <span>${this.translations.bio}</span>
            </div>
          `: ``}
        </div>
        <div class="item-content">
          <div class="item-title">
            <a href="/news/chart/details/${this.primaryChartData.stockmarket_index_id}" title="${this.translations.goToDetails}">
              <b>${this.primaryChartData.fullname}</b>
            </a>
          </div>
          <div class="item-category">${this.primaryChartData.sub_categoryname}</div>
          <div class="item-details">
            <div class="item-price">
              <b>${this.translations.price}:</b>
              <span>
                ${new Intl.NumberFormat(this.env.locale, {
                  style: 'currency',
                  currency: this.primaryChartData.currency_code,
                  minimumFractionDigits: 3
                }).format(this.primaryChartData.latest_price)}/${this.measurement}
              </span>
              ${getPriceDevelopmentIndicator(this.primaryChartData)}
            </div>
            <div class="item-country">
              <b>${this.translations.origin}:</b>
              <img class="flag-icon" src="/projects/application/dist/images/icons/flags/4x3/${this.primaryChartData.country_short.toLowerCase()}.svg" alt="" title="${this.primaryChartData.country_name}">
              <span>${this.primaryChartData.country_name}</span>
            </div>
            <div class="item-freight-terms">
              <b>${this.translations.incoType}:</b>
              ${this.primaryChartData.freight_term} (${this.primaryChartData.country_name})<br>
            </div>
            <div class="item-location">
              <b>${this.translations.location}:</b>
              ${this.primaryChartData.location}
            </div>
          </div>
        </div>
      </div>
    `;
  }
  
  get measurement() {
    let measurement = 'kg';

    switch (this.primaryChartData.measurement) {
        case 'Stück':
            measurement = `${this.translations.piece}`;
        break;
        case 'kg':
            measurement = `${this.translations.kg}`;
        break;
        case 'l':
            measurement = `${this.translations.l}`;
        break;
    }

    return measurement;
  }

  shrinkChart() {
    this.chart.options.layout.padding.right = 0;
    this.chart.options.scales.x.ticks.maxTicksLimit = 6;
    this.chart.options.scales.y.afterFit = scale => scale.width = 50;

    this.chart.update();
  }

  enlargeChart() {
    this.chart.options.layout.padding.right = 100;
    this.chart.options.scales.x.ticks.maxTicksLimit = 12;
    this.chart.options.scales.y.afterFit = scale => scale.width = 70;

    this.chart.update();
  }

  initChart() {
    const chartSettings = {
      type: 'line',
      data: {
        datasets: [{
          data: this.primaryChartData.euro_chartdata,
          backgroundColor: 'transparent',
          borderColor: this.colors[0],
          pointBackgroundColor: this.colors[0],
          pointBorderColor: this.colors[0],
        }],
      },
      plugins: [{
        beforeDraw: _ => {
          this.drawLimitLines(this.chart, this.minimizedChart, this.translations);

          // if the mouse cursor is outside the canvas, show the last tooltip after 1000ms
          if (this.showTooltipTimeout) clearTimeout(this.showTooltipTimeout);

          this.showTooltipTimeout = setTimeout(_ => this.showTooltip(this.chart, this.primaryChartData.chartdata.length - 1), 1000);
        },
        resize: _ => this.drawLimitLines(this.chart, this.minimizedChart, this.translations)
      }],
      options: {
        layout: {
          padding: {
            left: 0,
            right: this.minimizedChart ? 0 : 100,
            top: 0,
            bottom: 0
          }
        },
        bezierCurve: true,
        maintainAspectRatio: false,
        hover: {
          mode: 'nearest',
          intersect: false,
          axis: 'x',
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            mode: 'x',
            intersect: true,
            axis: 'x  ',
            callbacks: {
              label: context => {
                return new Intl.NumberFormat(getLanguageISO(), {
                  style: 'currency',
                  currency: this.currency,
                  minimumFractionDigits: 3
                }).format(Math.round(context.raw.y * 1000) / 1000) + ` / ` + this.measurement;
              },
              title: context => moment(context[0].raw.x).format('L')
            }
          }
        },
        scales: {
          x: {
            gridLines: {
              drawBorder: false,
              drawOnChartArea: true,
              tickMarkLength: 0,
              zeroLineColor: 'rgba(0, 0, 0, 0.1)',
            },
            type: 'time',

            // This seems to be broken because moment.localeData().longDateFormat('L') doesn't return the required format
            // time: {
            //   unit: 'day',
            //   unitStepSize: 1,
            //   displayFormats: {
            //     'day': moment.localeData().longDateFormat('L')
            //   }
            // },

            ticks: {
              maxTicksLimit: this.minimizedChart ? 6 : 12,
              lineHeight: 1,
              maxRotation: 45,
              minRotation: 45,
              autoSkip: true,
              padding: 10,
              callback: (value, index, values) => moment(values[index].value).format('L')
            },
            labels: this.primaryChartData.euro_chartdata.map(d => d.x)
          },
          y: {
            gridLines: {
              drawBorder: true,
              drawOnChartArea: true,
              tickMarkLength: 0
            },
            ticks: {
              autoSkip: true,
              maxTicksLimit: 7,
              padding: 5
            },
            title: {
              display: true,
              text: this.currency,
              padding: 0
            },
            padding: this.minimizedChart ? 0 : 20,
            afterFit: scale => scale.width = this.minimizedChart ? 50 : 70,
          },
        },
        responsive: true,
        elements: {
          line: {
            tension: 0,
            borderWidth: 1
          },
          point: {
            radius: 0,
            hitRadius: 5,
            hoverRadius: 3,
          }
        }
      }
    };

    this.chart = new Chart(this.chartCanvas, chartSettings);
  }

  drawVerticalLine(e) {
    this.verticalLineCanvas.width = this.chartCanvas.width;
    this.verticalLineCanvas.height = this.chartCanvas.height;
    const offsetLeft = this.chartWrap.getBoundingClientRect().left;
    const clientX = parseInt(e.clientX - offsetLeft);
    const ctx = this.verticalLineCanvas.getContext('2d');
    ctx.clearRect(0, 0, this.verticalLineCanvas.width, this.verticalLineCanvas.height);
    ctx.beginPath();
    ctx.moveTo((clientX + 0.5) * window.devicePixelRatio, this.chart.chartArea.top);
    ctx.lineTo((clientX + 0.5) * window.devicePixelRatio, this.chart.chartArea.bottom);
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = 'rgba(0, 113, 188, 0.75)';
    ctx.stroke();
  }
}